import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Share2, Download, Flame, Dumbbell, Brain,
  Heart, Gamepad2, Trophy, Sparkles, Target, Copy, Check,
  Leaf, Mountain, Sun, Moon, Zap, Shield
} from "lucide-react";
import { toast } from "sonner";
import { ShareButton } from "../components/ShareButton";
import { devWarn, devError } from "../utils/devLogger";

const ICON_MAP = {
  dumbbell: Dumbbell, brain: Brain, heart: Heart, flame: Flame,
  sparkles: Sparkles, leaf: Leaf, mountain: Mountain, sun: Sun,
  moon: Moon, zap: Zap, shield: Shield, target: Target,
};

const ProfileCard = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  const fetchCard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile/card`);
      setCard(res.data.card);
    } catch (error) {
      devError("Failed to fetch profile card:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `lumifit-${card?.name?.replace(/\s+/g, '-')?.toLowerCase() || 'profile'}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Profile card downloaded!");
    } catch (error) {
      toast.error("Failed to download. Try again.");
    }
  };

  const handleShare = async () => {
    const shareText = `Check out my LumiFit wellness profile! Body & Mind Score: ${Math.round(card?.score?.overall || 0)}/100 | Streak: ${card?.streak || 0} days`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: "My LumiFit Profile", text: shareText });
      } catch (e) {
        devWarn("Share dialog cancelled or failed");
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const AvatarLarge = () => {
    if (card?.avatar_type === "preset" && card?.avatar_preset) {
      const IconComp = ICON_MAP[card.avatar_preset.icon] || Sparkles;
      return (
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br border-4 border-white/30 shadow-xl flex items-center justify-center"
          style={{ backgroundImage: `linear-gradient(135deg, var(--tw-gradient-from), var(--tw-gradient-to))` }}
        >
          <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${card.avatar_preset.bg} flex items-center justify-center border-4 border-white/30 shadow-xl`}>
            <IconComp className="w-12 h-12 text-white" strokeWidth={1.5} />
          </div>
        </div>
      );
    }
    if (card?.picture) {
      const src = card.picture.startsWith("/api") ? `${API.replace('/api', '')}${card.picture}` : card.picture;
      return <img src={src} alt="" className="w-24 h-24 rounded-2xl object-cover border-4 border-white/30 shadow-xl" />;
    }
    return (
      <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center border-4 border-white/30 shadow-xl">
        <span className="text-4xl text-white font-light" style={{ fontFamily: 'Outfit' }}>{card?.name?.[0] || "?"}</span>
      </div>
    );
  };

  const ScoreMini = ({ value, label, color }) => {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;
    return (
      <div className="flex flex-col items-center">
        <div className="relative">
          <svg width="52" height="52" className="-rotate-90">
            <circle cx="26" cy="26" r={radius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
            <circle cx="26" cy="26" r={radius} fill="none" stroke={color} strokeWidth="3"
              strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-bold text-white">{Math.round(value)}</span>
          </div>
        </div>
        <span className="text-[10px] text-white/60 mt-1">{label}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="profile-card-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <ShareButton 
              text={`Check out my LumiFit wellness profile! Body & Mind Score: ${Math.round(card?.score?.overall || 0)}/100`}
              url={window.location.href}
            />
            <Button variant="outline" size="sm" onClick={handleShare} className="gap-2" data-testid="share-profile-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button size="sm" onClick={handleDownload} className="glow-button gap-2" data-testid="download-profile-btn">
              <Download className="w-4 h-4" />
              Download
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-md mx-auto">
          {/* The shareable card */}
          <div
            ref={cardRef}
            className="rounded-3xl overflow-hidden shadow-2xl"
            data-testid="profile-card"
          >
            {/* Card Header - gradient */}
            <div className="relative bg-gradient-to-br from-slate-900 via-sky-950 to-indigo-950 px-8 pt-10 pb-8">
              {/* Glow effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-sky-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl" />
              
              {/* Logo */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
                </div>
                <span className="text-sm text-white/60" style={{ fontFamily: 'Outfit' }}>LumiFit</span>
              </div>

              {/* Profile Info */}
              <div className="flex items-start gap-5">
                <AvatarLarge />
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-medium text-white truncate" style={{ fontFamily: 'Outfit' }}>
                    {card?.name}
                  </h2>
                  {card?.bio && (
                    <p className="text-sm text-white/50 mt-1 line-clamp-2">{card.bio}</p>
                  )}
                  {card?.fitness_goal && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <Target className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.5} />
                      <span className="text-xs text-sky-300">{card.fitness_goal}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Body & Mind Score */}
              {card?.score && (
                <div className="mt-8">
                  <div className="text-center mb-4">
                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Body & Mind Score</p>
                    <p className="text-5xl font-light text-white" style={{ fontFamily: 'Outfit' }}>
                      {Math.round(card.score.overall)}
                      <span className="text-lg text-white/30">/100</span>
                    </p>
                  </div>
                  <div className="flex justify-center gap-5">
                    <ScoreMini value={card.score.physical} label="Physical" color="#06b6d4" />
                    <ScoreMini value={card.score.cognitive} label="Cognitive" color="#818cf8" />
                    <ScoreMini value={card.score.consistency} label="Consistency" color="#f97316" />
                    <ScoreMini value={card.score.wellness} label="Wellness" color="#22c55e" />
                  </div>
                </div>
              )}
            </div>

            {/* Card Stats */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 px-8 py-6">
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{card?.streak || 0}</p>
                  <p className="text-[10px] text-white/40">Day Streak</p>
                </div>
                <div className="text-center">
                  <Dumbbell className="w-5 h-5 text-cyan-400 mx-auto mb-1" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{card?.total_exercises || 0}</p>
                  <p className="text-[10px] text-white/40">Exercises</p>
                </div>
                <div className="text-center">
                  <Gamepad2 className="w-5 h-5 text-indigo-400 mx-auto mb-1" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{card?.highest_streak || 0}</p>
                  <p className="text-[10px] text-white/40">Best Streak</p>
                </div>
                <div className="text-center">
                  <Heart className="w-5 h-5 text-pink-400 mx-auto mb-1" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{card?.total_meditation_minutes || 0}</p>
                  <p className="text-[10px] text-white/40">Med. Min</p>
                </div>
              </div>

              {/* Top Achievements */}
              {card?.top_achievements?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-white/10">
                  <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Top Achievements</p>
                  <div className="flex gap-3">
                    {card.top_achievements.map((ach) => (
                      <div key={ach.achievement_id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                        <Trophy className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                        <span className="text-xs text-white/70">{ach.achievement_id.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-5 pt-4 border-t border-white/5 text-center">
                <p className="text-[10px] text-white/20">lumifit.me</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-sm text-slate-500">
              Download your profile card to share on social media
            </p>
            <p className="text-xs text-slate-400">
              Or use the Share button to copy stats to clipboard
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileCard;
